import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';
import './CounselingInsightPage.css';

interface CounselingInsights {
  goal_accuracy: {
    period: string;
    actual_average_study_hours: number;
    accuracy_scores: {
      student: any;
      staff: any;
      parent: any;
    };
    insights: {
      most_accurate: string;
      needs_adjustment: string[];
      summary: string;
    };
  };
  study_patterns: {
    weekday_analysis: Record<string, number>;
    best_day: string;
    worst_day: string;
    consistency_score: number;
    max_streak: number;
    current_streak: number;
  };
  goal_history: {
    total_goals: number;
    achieved_goals: number;
    achievement_rate: number;
    trend: string;
  };
  gap_analysis: Record<string, {
    gap: number;
    status: string;
    needs_discussion: boolean;
  }>;
  counseling_points: Array<{
    priority: string;
    category: string;
    title: string;
    description: string;
    action: string;
  }>;
  praise_points: string[];
  improvement_areas: string[];
}

const CounselingInsightPage: React.FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const [insights, setInsights] = useState<CounselingInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weeks, setWeeks] = useState(4);

  useEffect(() => {
    fetchInsights();
  }, [studentId, weeks]);

  const fetchInsights = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        console.error('❌ No token found, redirecting to login');
        alert('로그인이 필요합니다. 로그인 페이지로 이동합니다.');
        // localhost, 스테이징 서버에서는 /studyroom prefix 사용
        const hostname = window.location.hostname;
        const loginPath = (hostname === 'localhost' || hostname === '192.168.0.38' || hostname === '180.70.238.90')
          ? '/studyroom/owner/login'
          : '/owner/login';
        navigate(loginPath);
        return;
      }

      const url = `${API_BASE_URL}/api/study-goals/student/${studentId}/counseling-insights?weeks=${weeks}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });


      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ API Error:', errorData);

        if (response.status === 401 || response.status === 403) {
          alert('인증이 만료되었습니다. 다시 로그인해주세요.');
          localStorage.removeItem('token');
          // localhost, 스테이징 서버에서는 /studyroom prefix 사용
          const hostname = window.location.hostname;
          const loginPath = (hostname === 'localhost' || hostname === '192.168.0.38' || hostname === '180.70.238.90')
            ? '/studyroom/owner/login'
            : '/owner/login';
          navigate(loginPath);
          return;
        }

        throw new Error(errorData.detail || '인사이트 조회 실패');
      }

      const data = await response.json();
      setInsights(data);
    } catch (err: any) {
      console.error('❌ Error fetching insights:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#ff4d4f';
      case 'medium': return '#faad14';
      case 'low': return '#52c41a';
      default: return '#999';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aligned': return '#52c41a';
      case 'moderate_gap': return '#faad14';
      case 'high_gap': return '#ff4d4f';
      default: return '#999';
    }
  };

  const getTrendEmoji = (trend: string) => {
    switch (trend) {
      case 'improving': return '📈';
      case 'declining': return '📉';
      case 'stable': return '➡️';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <div className="counseling-insight-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>상담 인사이트 생성 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="counseling-insight-page">
        <div className="error">
          <h2>⚠️ 오류 발생</h2>
          <p>{error}</p>
          <button onClick={fetchInsights}>다시 시도</button>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="counseling-insight-page">
        <div className="no-data">
          <p>데이터가 없습니다</p>
        </div>
      </div>
    );
  }

  return (
    <div className="counseling-insight-page">
      <div className="page-header">
        <h1>📊 상담 인사이트</h1>
        <div className="period-selector">
          <button
            className={weeks === 2 ? 'active' : ''}
            onClick={() => setWeeks(2)}
          >
            2주
          </button>
          <button
            className={weeks === 4 ? 'active' : ''}
            onClick={() => setWeeks(4)}
          >
            4주
          </button>
          <button
            className={weeks === 8 ? 'active' : ''}
            onClick={() => setWeeks(8)}
          >
            8주
          </button>
          <button
            className={weeks === 12 ? 'active' : ''}
            onClick={() => setWeeks(12)}
          >
            12주
          </button>
        </div>
      </div>

      <div className="insights-container">
        {/* 칭찬 포인트 */}
        <section className="section praise-section">
          <h2>⭐ 칭찬할 점</h2>
          <div className="praise-list">
            {insights.praise_points.length > 0 ? (
              insights.praise_points.map((point, idx) => (
                <div key={idx} className="praise-item">
                  <span className="praise-icon">✨</span>
                  <span>{point}</span>
                </div>
              ))
            ) : (
              <p className="empty-message">칭찬 포인트가 없습니다</p>
            )}
          </div>
        </section>

        {/* 상담 포인트 */}
        <section className="section counseling-points-section">
          <h2>💬 상담 포인트</h2>
          <div className="counseling-points-list">
            {insights.counseling_points.length > 0 ? (
              insights.counseling_points.map((point, idx) => (
                <div
                  key={idx}
                  className="counseling-point-card"
                  style={{ borderLeft: `4px solid ${getPriorityColor(point.priority)}` }}
                >
                  <div className="point-header">
                    <span
                      className="priority-badge"
                      style={{ backgroundColor: getPriorityColor(point.priority) }}
                    >
                      {point.priority.toUpperCase()}
                    </span>
                    <h3>{point.title}</h3>
                  </div>
                  <p className="description">{point.description}</p>
                  <div className="action">
                    <strong>권장 조치:</strong> {point.action}
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-message">상담 포인트가 없습니다</p>
            )}
          </div>
        </section>

        {/* 목표 정확도 */}
        <section className="section accuracy-section">
          <h2>🎯 목표 정확도</h2>
          <p className="period">{insights.goal_accuracy.period}</p>
          <p className="actual-hours">
            실제 평균: <strong>{insights.goal_accuracy.actual_average_study_hours.toFixed(1)}시간/일</strong>
          </p>

          <div className="accuracy-grid">
            {['student', 'staff', 'parent'].map(setter => {
              const score = insights.goal_accuracy.accuracy_scores[setter as keyof typeof insights.goal_accuracy.accuracy_scores];
              return (
                <div key={setter} className="accuracy-card">
                  <h4>{setter === 'student' ? '학생' : setter === 'staff' ? '멘토' : '학부모'}</h4>
                  {score.has_goal ? (
                    <>
                      <div className="accuracy-score">{score.accuracy.toFixed(1)}점</div>
                      <div className="goal-info">
                        <p>목표: {score.goal_average.toFixed(1)}시간</p>
                        <p className={score.gap_hours >= 0 ? 'gap-positive' : 'gap-negative'}>
                          갭: {score.gap_hours > 0 ? '+' : ''}{score.gap_hours.toFixed(1)}시간
                        </p>
                      </div>
                      <div className="trend">{score.trend}</div>
                    </>
                  ) : (
                    <div className="no-goal">목표 없음</div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="accuracy-summary">
            <p>가장 정확: <strong>{insights.goal_accuracy.insights.most_accurate}</strong></p>
            {insights.goal_accuracy.insights.needs_adjustment.length > 0 && (
              <p>조정 필요: <strong>{insights.goal_accuracy.insights.needs_adjustment.join(', ')}</strong></p>
            )}
          </div>
        </section>

        {/* 3자 갭 분석 */}
        <section className="section gap-section">
          <h2>🔍 3자 목표 갭 분석</h2>
          <div className="gap-grid">
            {Object.entries(insights.gap_analysis).map(([key, value]) => (
              <div
                key={key}
                className="gap-card"
                style={{ borderColor: getStatusColor(value.status) }}
              >
                <h4>{key.replace('_', ' vs ')}</h4>
                <div className="gap-value">{value.gap.toFixed(1)}h</div>
                <div
                  className="gap-status"
                  style={{ color: getStatusColor(value.status) }}
                >
                  {value.status}
                </div>
                {value.needs_discussion && (
                  <div className="discussion-needed">🔴 대화 필요</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* 학습 패턴 */}
        <section className="section pattern-section">
          <h2>📈 학습 패턴</h2>
          <div className="weekday-chart">
            {Object.entries(insights.study_patterns.weekday_analysis).map(([day, hours]) => (
              <div key={day} className="day-column">
                <div
                  className="bar"
                  style={{
                    height: `${Math.max(hours * 20, 10)}px`,
                    backgroundColor: day === insights.study_patterns.best_day ? '#52c41a' :
                                   day === insights.study_patterns.worst_day ? '#ff4d4f' : '#1890ff'
                  }}
                >
                  <span className="hours">{hours.toFixed(1)}h</span>
                </div>
                <div className="day-label">{day}</div>
              </div>
            ))}
          </div>

          <div className="pattern-summary">
            <div className="summary-item">
              <span className="label">최고 요일:</span>
              <span className="value">{insights.study_patterns.best_day}</span>
            </div>
            <div className="summary-item">
              <span className="label">최저 요일:</span>
              <span className="value">{insights.study_patterns.worst_day}</span>
            </div>
            <div className="summary-item">
              <span className="label">일관성:</span>
              <span className="value">{insights.study_patterns.consistency_score.toFixed(1)}점</span>
            </div>
          </div>
        </section>

        {/* 목표 히스토리 */}
        <section className="section history-section">
          <h2>📝 목표 히스토리</h2>
          <div className="history-stats">
            <div className="stat-item">
              <div className="stat-label">전체 목표</div>
              <div className="stat-value">{insights.goal_history.total_goals}개</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">달성</div>
              <div className="stat-value">{insights.goal_history.achieved_goals}개</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">달성률</div>
              <div className="stat-value">{insights.goal_history.achievement_rate.toFixed(1)}%</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">추세</div>
              <div className="stat-value">
                {getTrendEmoji(insights.goal_history.trend)} {insights.goal_history.trend}
              </div>
            </div>
          </div>
        </section>

        {/* 개선 영역 */}
        <section className="section improvement-section">
          <h2>📌 개선 필요 영역</h2>
          <div className="improvement-list">
            {insights.improvement_areas.length > 0 ? (
              insights.improvement_areas.map((area, idx) => (
                <div key={idx} className="improvement-item">
                  <span className="improvement-icon">📍</span>
                  <span>{area}</span>
                </div>
              ))
            ) : (
              <p className="empty-message">개선 필요 영역이 없습니다</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CounselingInsightPage;
